import React, { useEffect, useRef, useState } from "react";

import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons";
import "./guestPicker.scss";

export interface IRoom {
  adults: number;
  children: number;
}

interface IGuestPickerProps {
  rooms: IRoom[];
  onRoomsChange: (updated: IRoom[]) => void;
  hideLabel?: boolean;
}

export default function GuestPicker({ rooms, onRoomsChange, hideLabel }: IGuestPickerProps) {
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [internalRooms, setInternalRooms] = useState<IRoom[]>(rooms);

  // Sync internal state with props when dropdown opens or props change
  useEffect(() => {
    setInternalRooms(rooms);
  }, [rooms, isDropdownOpen]);

  // Toggle dropdown
  const toggleDropdown = () => setIsDropdownOpen(prev => !prev);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setInternalRooms(rooms); // Reset to original state
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [rooms]);

  // Handlers
  const handleRoomChange = (index: number, field: "adults" | "children", value: number) => {
    const updated = [...internalRooms];
    updated[index] = { ...updated[index], [field]: value };
    setInternalRooms(updated);
  };

  // const handleAddRoom = () => {
  //   setInternalRooms([...internalRooms, { adults: 1, children: 0 }]);
  // };

  const handleRemoveRoom = (index: number) => {
    const updated = internalRooms.filter((_, i) => i !== index);
    setInternalRooms(updated);
  };

  const handleDone = () => {
    onRoomsChange(internalRooms);
    setIsDropdownOpen(false);
  };

  const totalGuests = rooms.reduce((acc, room) => acc + room.adults + room.children, 0);

  return (
    <div className="guest-picker-container" ref={dropdownRef}>
      {!hideLabel && <p className="guest-picker-container_label">Guests</p>}

      {/* Selector bar */}
      <div
        className={classNames("guest-picker-bar", {
          "guest-picker-bar_active": isDropdownOpen,
        })}
        onClick={toggleDropdown}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="16" fill="none">
          <path
            d="M0.833252 15C2.77974 12.9355 5.42243 11.6667 8.33325 11.6667C11.2441 11.6667 13.8868 12.9355 15.8333 15M12.0833 4.58337C12.0833 6.65444 10.4043 8.33337 8.33325 8.33337C6.26218 8.33337 4.58325 6.65444 4.58325 4.58337C4.58325 2.51231 6.26218 0.833374 8.33325 0.833374C10.4043 0.833374 12.0833 2.51231 12.0833 4.58337Z"
            stroke="#667085"
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <span className="summary-text">
          {totalGuests} guest{totalGuests !== 1 ? "s" : ""}, {rooms.length} room{rooms.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Dropdown */}
      {isDropdownOpen && (
        <div className="guest-picker-dropdown">
          {internalRooms.map((room, index) => (
            <div key={index} className="room-section">
              <div className="room-header">
                <p>Room {index + 1}</p>

                {index > 0 && (
                  <button type="button" className="remove-room-button" onClick={() => handleRemoveRoom(index)}>
                    <FontAwesomeIcon icon={faMinus} />
                  </button>
                )}
              </div>

              <hr />

              {/* Adults */}
              <div className="guest-row">
                <span className="guest-label">Adults</span>

                <div className="guest-counter">
                  <button
                    type="button"
                    className="counter-button"
                    disabled={room.adults <= 1}
                    onClick={() => handleRoomChange(index, "adults", Math.max(1, room.adults - 1))}
                  >
                    <FontAwesomeIcon icon={faMinus} />
                  </button>

                  <span className="counter-value">{room.adults}</span>

                  <button
                    type="button"
                    className="counter-button"
                    onClick={() => handleRoomChange(index, "adults", room.adults + 1)}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
              </div>

              {/* Children */}
              <div className="guest-row">
                <span className="guest-label">Children</span>

                <div className="guest-counter">
                  <button
                    type="button"
                    className="counter-button"
                    disabled={room.children <= 0}
                    onClick={() => handleRoomChange(index, "children", Math.max(0, room.children - 1))}
                  >
                    <FontAwesomeIcon icon={faMinus} />
                  </button>

                  <span className="counter-value">{room.children}</span>

                  <button
                    type="button"
                    className="counter-button"
                    onClick={() => handleRoomChange(index, "children", room.children + 1)}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* <button className="add-room-button" type="button" onClick={handleAddRoom}>
            Add another room
          </button> */}

          <button className="done-button" type="button" onClick={handleDone}>
            Done
          </button>
        </div>
      )}
    </div>
  );
}
